// Shared pure-UI primitives used across the Analyze feature

import { Box, Typography, Tooltip, alpha, useTheme } from '@mui/material';

// ── Circular SVG progress ring ───────────────────────────────────────────────
export function ScoreRing({ value, max = 100, color, size = 96, label, sub }) {
    const pct = Math.round((value / max) * 100);
    const r = (size - 12) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(color, 0.12)} strokeWidth={10} />
                    <circle
                        cx={size / 2} cy={size / 2} r={r} fill="none"
                        stroke={color} strokeWidth={10}
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.8s ease' }}
                    />
                </svg>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, color, lineHeight: 1, fontSize: size > 80 ? '1.2rem' : '1rem' }}
                    >
                        {value}{max !== 100 ? `/${max}` : '%'}
                    </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.75rem' }}>
                {label}
            </Typography>
            {sub && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', textAlign: 'center' }}>
                    {sub}
                </Typography>
            )}
        </Box>
    );
}

// ── Confidence badge — small chip indicating how much to trust a card's result
// Every engine return includes a `confidence: {level, reason, score}` object.
// This badge surfaces the level as a compact pill and exposes the reason on hover.
export function ConfidenceBadge({ confidence, size = 'small' }) {
    const theme = useTheme();
    if (!confidence || !confidence.level) return null;
    const level = confidence.level;
    const labelMap = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' };
    const colorMap = {
        high: theme.palette.success.main,
        medium: theme.palette.warning.main,
        low: theme.palette.error.main,
    };
    const color = colorMap[level] || theme.palette.text.secondary;
    const isCompact = size === 'small';
    return (
        <Tooltip
            arrow
            title={confidence.reason || 'Confidence reflects how much data backs this result'}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: isCompact ? 0.75 : 1,
                    py: isCompact ? 0.125 : 0.25,
                    borderRadius: '999px',
                    bgcolor: alpha(color, 0.12),
                    border: `1px solid ${alpha(color, 0.35)}`,
                    color,
                    fontSize: isCompact ? '0.65rem' : '0.72rem',
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                    cursor: 'help',
                    whiteSpace: 'nowrap',
                }}
            >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                {labelMap[level]}
            </Box>
        </Tooltip>
    );
}

// ── Coloured metric tile — square icon box (not circular Avatar) ──────────────
export function MetricTile({ icon, label, value, sub, color }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    return (
        <Box sx={{
            p: 2.5, borderRadius: '12px',
            background: isDark ? alpha(color, 0.08) : alpha(color, 0.05),
            border: `1px solid ${alpha(color, isDark ? 0.2 : 0.15)}`,
            borderLeft: `4px solid ${color}`,
            display: 'flex', flexDirection: 'column', gap: 0.5,
        }}>
            <Box sx={{
                width: 32, height: 32, borderRadius: '8px', mb: 0.5,
                bgcolor: alpha(color, isDark ? 0.2 : 0.12),
                color, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {icon}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
                {value}
            </Typography>
            {sub && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>{sub}</Typography>}
        </Box>
    );
}
